const number = value => value == null || String(value).trim() === "" ? NaN : Number(String(value).replace(",", "."));

// Summary efficiencies only: do not alter coefficients or sampled curve data.
// At each measured voltage, Sandia is quadratic in DC power. Its efficiency
// has at most one interior stationary point, so no sampling resolution is used.
export function sandiaEfficiencies(fit) {
  if (!fit) return null;
  const { Paco, Pdco, Vdco, Pso, C0, C1, C2, C3 } = Object.fromEntries(
    ["Paco", "Pdco", "Vdco", "Pso", "C0", "C1", "C2", "C3"].map(key => [key, number(fit[key])])
  );
  if (![Paco, Pdco, Vdco, Pso, C0, C1, C2, C3].every(Number.isFinite) || !(Paco > 0 && Pdco > 0)) return null;
  const nominal = Paco / Pdco;
  let peak = nominal;
  const voltages = new Set([Vdco, ...(fit.cec_dc_voltage || []).map(number)]);
  for (const voltage of voltages) {
    const delta = voltage - Vdco;
    const A = Pdco * (1 + C1 * delta), B = Pso * (1 + C2 * delta), C = C0 * (1 + C3 * delta);
    if (!Number.isFinite(A + B + C) || !(A > B && B >= 0)) return null;
    const slope = Paco / (A - B) - C * (A - B);
    // Require a monotonic output up to rated power; reject unphysical fits.
    if (slope < 0 || slope + 2 * C * (A - B) < 0) return null;
    const constant = C * B * B - slope * B;
    const candidates = [A];
    const stationary = Math.sqrt(constant / C);
    if (stationary > B && stationary < A) candidates.push(stationary);
    if (B === 0) peak = Math.max(peak, slope);
    for (const power of candidates) {
      const output = slope * (power - B) + C * (power - B) ** 2;
      peak = Math.max(peak, output / power);
    }
  }
  if (!(nominal > 0 && nominal <= 1 && peak > 0 && peak <= 1)) return null;
  return { eff_nom: nominal, eff_max: peak };
}

// A fit is valid only for the exact power inputs that produced it. Never let
// staged coefficients silently overwrite newer edits in the parent form.
export function currentSandiaFit(fit, form) {
  if (!fit || !(number(form.Paco) > 0) || !(number(form.Pnt) >= 0)) return null;
  if (number(fit.Paco) !== number(form.Paco) || number(fit.Pnt) !== number(form.Pnt)) return null;
  if (!["Pdco", "Vdco", "Pso", "C0", "C1", "C2", "C3"].every(key => Number.isFinite(number(fit[key])))) return null;
  return fit;
}

export function curveCharacteristicsForForm(fit, form) {
  if (!fit) return null;
  if (currentSandiaFit(fit, form)) return fit;
  const oldPower = number(fit.Paco), newPower = number(form.Paco);
  const ratio = oldPower > 0 && newPower > 0 ? newPower / oldPower : 1;
  // Preserve the manufacturer's LOAD/efficiency points on reopen. Rebuild
  // their absolute powers if Paco changed, but discard all stale coefficients.
  return { Paco: oldPower * ratio, Pnt: form.Pnt,
    cec_ac_power: fit.cec_ac_power?.map(value => value * ratio),
    cec_dc_power: fit.cec_dc_power?.map(value => value * ratio),
    cec_dc_voltage: fit.cec_dc_voltage,
    cec_dc_voltagelevel: fit.cec_dc_voltagelevel };
}
